import { useTranslation } from "react-i18next";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { FormProvider, useForm } from "react-hook-form";
import { ExpiredActionMessageTemplateReturn, IExpiredActionExecuteParams, IExpiredActionInsertionForm } from "../models/models";
import { EXPIRED_ACTION_INSERTION_FORM_VALIDATION_SCHEMA, EXPIRED_ACTION_INSERTION_INITIAL_VALUE, EXPIRED_PRACTICES_QUERY_KEY, ExpiredInsertionAction, SMS_MAX_CHAR, TEXT_MAX_CHAR } from "../config";
import Grid from "@mui/material/Grid";
import { FormTextField } from "../common/form-fields-wrappers";
import { Button, Stack } from "@mui/material/index";
import TextCard from "./component/TextCard";
import RaDetails from "./ra-details/RaDetails";
import ReturnDetails from "./return-details/ReturnDetails";
import { yupResolver } from "@hookform/resolvers/yup";
import ContentBody from "../common/content-body/ContentBody";
import { useMutation, useQuery } from "@tanstack/react-query";
import { executeExpiredAction, getExpiredActionsMessageTemplate } from "../services/http.services";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { useCallback, useContext, useEffect, useState } from "react";
import { snackbarTypes } from "../../../util/state/globalVariables";
import CustomLoader from "../../../util/component/CustomLoader";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { expiredActionPropsState, practiceDetail } from "../state";
import { setSnackbar } from "../../../util/state/globalAction";

interface ActionInsertionProps {
    onConfirm: () => void,
    onClose: () => void
}

const ActionInsertion = ({...props}: ActionInsertionProps) => {

    const {t, i18n} = useTranslation(['expiredManagement']);
    const theme = useTheme();
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const [isMultipleCodes, setIsMultipleCodes] = useState(false);

    const expiredActionProps = useRecoilValue(expiredActionPropsState);
    const setPracticeDetail = useSetRecoilState(practiceDetail);

    const epiredActionInsertionForm = useForm<IExpiredActionInsertionForm>({
        defaultValues: EXPIRED_ACTION_INSERTION_INITIAL_VALUE,
        resolver: yupResolver(EXPIRED_ACTION_INSERTION_FORM_VALIDATION_SCHEMA),
        context: {
            bodyMaxChar: (expiredActionProps?.actionType == ExpiredInsertionAction.NEW_SMS) ? SMS_MAX_CHAR : TEXT_MAX_CHAR,
            bodyMaxCharTestRequired: ![ExpiredInsertionAction.SMS1, ExpiredInsertionAction.SMS2, ExpiredInsertionAction.SMS3, ''].includes(expiredActionProps?.actionType || '')
        }
    });
   
    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message, t('common.message.error.tryLater')),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const onConfirm = useCallback((formData: IExpiredActionInsertionForm) => {

        const req: IExpiredActionExecuteParams = {
            permissionData: {
                mobilityStores: globalData?.user?.mobilityStores?.map(((elem:any) => elem.sapId)),
                authorities: globalData?.user?.scopes
            },
            action: expiredActionProps?.actionType || '',
            expiredCodes: expiredActionProps?.expiredCodes || [],
            subject: formData.subject || undefined,
            body: formData.body,
            templateCode: formData.code,
            language: formData.language,
            attachment: [...formData.attachment],
        }

        executeMutation.mutate(req);

    }, [globalData, expiredActionProps]);

    const getActionMessageTemplateQuery = useQuery({
        queryKey: [EXPIRED_PRACTICES_QUERY_KEY.GET_ACTION_MESSAGE_TEMPLATE],
        enabled: false,
        queryFn: () => {
            if(!!expiredActionProps?.actionType && !!expiredActionProps?.expiredCodes && expiredActionProps?.expiredCodes.length > 0)
                return getExpiredActionsMessageTemplate(expiredActionProps.expiredCodes, expiredActionProps.actionType);
        },
        onSuccess: (data) => {

            const template: ExpiredActionMessageTemplateReturn = data?.data;

            epiredActionInsertionForm.reset({
                subject: template.subject || '',
                body: template.body || '',
                code: template.code,
                attachment: template.attachment ? template.attachment?.map(attach => ({
                    filename: attach.filename || '',
                    url: attach.url || ''
                })) || [] : [],
                language: i18n.languages?.at(0) || '',
                //operator: globalData?.user?.name || '',
                raDetails: expiredActionProps?.raDetails,
                returnDetails: expiredActionProps?.returnDetails
            });
            
        },
        onError: (error: any) => {
            viewSnackbarMessage(`common.message.error.getActionTemplate.${error?.response?.status}`, snackbarTypes.error);
            props.onClose();
        },
    });

    const executeMutation = useMutation(
        {
            mutationKey: [EXPIRED_PRACTICES_QUERY_KEY.EXECUTE_ACTION],
            mutationFn: (params: IExpiredActionExecuteParams) => {
                return executeExpiredAction(params);
            },
            onSuccess: (response) => {
                viewSnackbarMessage('common.message.success', snackbarTypes.success);

                if(!!response?.data && !isMultipleCodes)
                    setPracticeDetail(response.data);

                props.onConfirm();
            },
            onError: (error: any) => {
                viewSnackbarMessage(`common.message.error.execute.${error?.response?.status}`, snackbarTypes.error);
            }
        }
    )

    useEffect(()=> {  
        if(!!expiredActionProps) {
            (expiredActionProps.expiredCodes.length > 1) && setIsMultipleCodes(true)
            getActionMessageTemplateQuery.refetch();
        }

    },[expiredActionProps]);

    return (
        <>
            <FormProvider {...epiredActionInsertionForm} >
                <ContentBody >
                    <Paper elevation={1} sx={{p: theme.spacing(2)}}>
                        <Grid container spacing={theme.spacing(2)}>
                            {isMultipleCodes && 
                                <Grid item xs={12}>
                                    <TextCard />
                                </Grid>
                            }
                            {!isMultipleCodes &&
                                <>
                                    {/*<Grid item xs={12} sm={6}>
                                        <FormTextField<IExpiredActionInsertionForm> 
                                            name="operator"
                                            label={t("Operatore")}
                                            disabled
                                        />
                                        
                                    </Grid>*/}
                                    <Grid item container xs={12} columnSpacing={theme.spacing(2)} rowSpacing={theme.spacing(2)} sx={{ display: "flex", alignItems: "baseline" }}>
                                        <Grid item container xs={12} sm={6}>
                                            <Grid item xs={12}>
                                                <FormTextField<IExpiredActionInsertionForm>
                                                    name="raDetails.phoneNumber"
                                                    label={t('expiredDetail.form.customerMobile')}
                                                    disabled />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <FormTextField<IExpiredActionInsertionForm>
                                                    name="raDetails.rentalType"
                                                    label={t('expiredDetail.form.rentalType')}
                                                    disabled />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextCard />
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} mt={theme.spacing(2)}>
                                        <RaDetails />
                                    </Grid>
                                    <Grid item xs={12} mt={theme.spacing(2)}>
                                        <ReturnDetails />
                                    </Grid>
                                </>
                            }
                        </Grid>

                        <Stack 
                            direction="row" 
                            alignItems={"center"} 
                            justifyContent={"flex-end"} 
                            columnGap={1}
                            mt={theme.spacing(6)} 
                            mb={{xs: 1}}
                        >
                            <Button
                                color="accent"
                                disabled={expiredActionProps?.expiredCodes?.length == 0}
                                onClick={epiredActionInsertionForm.handleSubmit(onConfirm)}
                            >
                                {t('common.button.send')}
                            </Button>
                        </Stack>  
                    </Paper>
                    <CustomLoader loading={getActionMessageTemplateQuery.isFetching || executeMutation.isLoading} />
                </ContentBody>
            </FormProvider>
        </>
    )
}

export default ActionInsertion;